import React from 'react';
import '../../TestView/styles.scss';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import strings from 'utils/strings';
import {Link} from 'react-router-dom';
import handBookPdf from 'assets/Handbok Dysmate-Y SE version 1.0_compressed.pdf';
import {
	CHILD_SCRNG_TEST_STEP_IDS,
	YOUTH_TEST_STEP_IDS,
} from '../../../constants';
import {getPronoun} from 'utils/getPronouns';
import {formateTestName} from 'utils/formatTestName';

const TESTS_REQUIRED_TO_PASS = 3;

const CHILD_SCRNG_IDS = {
	STAVNING: 1,
	ORDKEDJOR: 2,
	BYTA_LJUD: 3,
	SKRIVFLYT: 4,
	LASFORSTAELSE: 5,
	ORDFORSTAELSE: 6,
};

class Assessment extends React.Component {
	constructor(props) {
		super(props);
	}

	// eslint-disable-next-line complexity
	render() {
		const {testResult, completeName, firstName} = this.props;

		const allLowZscores = {};

		let zScoreLowCount = 0;
		let writeUpText;
		let clearlyBelowZScoreCount = 0;
		let significantlyBelowZScoreCount = 0;
		let withInZScoreCount = 0;
		let clearlyAboveZScoreCount = 0;

		const isChildScreening = testResult.type === 'child-screening';
		const pronoun = getPronoun(testResult.testUser.gender);
		const possessivePronoun =
			pronoun === 'han' ? 'hans' : pronoun === 'hon' ? 'hennes' : 'hens';

		const testType = isChildScreening
			? CHILD_SCRNG_TEST_STEP_IDS
			: YOUTH_TEST_STEP_IDS;

		const handbookLink = (
			<Link
				style={{color: 'blue'}}
				to={handBookPdf}
				target="_blank"
				rel="noopener noreferrer"
			>
				{strings.testView.handbokDysmate}
			</Link>
		);

		testResult.subtests.forEach((subtest) => {
			if (!subtest) return;

			const {id} = subtest;
			const zScore = Number(subtest.zScore);

			if (!Number.isFinite(zScore)) return;

			if (zScore < -1) {
				zScoreLowCount++;
				allLowZscores[id] = zScore;
			}

			if (zScore <= -2.01) {
				significantlyBelowZScoreCount++;
			} else if (zScore <= -1.01) {
				clearlyBelowZScoreCount++;
			} else if (zScore >= -1.0 && zScore <= 1.0) {
				withInZScoreCount++;
			} else if (zScore > 1.0) {
				clearlyAboveZScoreCount++;
			}
		});

		const allLowZscoresTestsWithNames = {};
		for (const [key, value] of Object.entries(testType)) {
			if (allLowZscores[value] !== undefined) {
				allLowZscoresTestsWithNames[key] = allLowZscores[value];
			}
		}
		const keys = Object.keys(allLowZscoresTestsWithNames);

		const formatSwedishList = (items) => {
			if (!items?.length) return '';
			if (items.length === 1) return items[0];
			if (items.length === 2) return `${items[0]} och ${items[1]}`;
			return `${items.slice(0, -1).join(', ')} och ${items[items.length - 1]}`;
		};

		const belowParts = [];
		if (clearlyBelowZScoreCount > 0) {
			belowParts.push(
				strings.formatString(strings.testView.klartUnderMedRange, clearlyBelowZScoreCount)
			);
		}
		if (significantlyBelowZScoreCount > 0) {
			belowParts.push(
				strings.formatString(strings.testView.betydligtUnderMedRange, significantlyBelowZScoreCount)
			);
		}

		const belowSummary = strings.formatString(strings.testView.klartUnderMedRange, clearlyBelowZScoreCount) +
			(significantlyBelowZScoreCount > 0
				? strings.formatString(strings.testView.ochBetydligtUnderMedRange, significantlyBelowZScoreCount)
				: '');

		const remainderText =
			withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
				? strings.formatString(strings.testView.resterandeDeltesternaUppnar, completeName)
				: '';

		// ---------------------------
		if (isChildScreening) {
			const finishedCount =
				testResult.subtests?.filter((s) => {
					return s && Number.isFinite(Number(s.zScore));
				}).length || 0;
			const missingCount = 6 - finishedCount;
			const isUnfinished =
				testResult.completedAt === null || missingCount >= 2;

			const lowTestNames = keys.map((k) => `«${formateTestName(k)}»`);
			const lowList = formatSwedishList(lowTestNames);

			const isBytaLow =
				allLowZscores[CHILD_SCRNG_IDS.BYTA_LJUD] !== undefined;
			const hasLowReading =
				allLowZscores[CHILD_SCRNG_IDS.LASFORSTAELSE] !== undefined;
			const hasLowWordComp =
				allLowZscores[CHILD_SCRNG_IDS.ORDFORSTAELSE] !== undefined;
			const weakOtherThanBytaCount = isBytaLow
				? zScoreLowCount - 1
				: zScoreLowCount;
			const useText3 = isBytaLow && zScoreLowCount <= 2;

			const useText2 =
				zScoreLowCount === 2 && hasLowReading && hasLowWordComp;

			const useText1 =
				zScoreLowCount >= 3 ||
				(zScoreLowCount === 2 && !useText2 && !useText3);

			if (isUnfinished) {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text5,
					`${completeName}`
				);
			} else if (zScoreLowCount === 0) {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text4,
					`${completeName}`,
					`${withInZScoreCount + clearlyAboveZScoreCount}`
				);
			} else if (useText3) {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text3,
					`${completeName}`,
					belowSummary,
					remainderText,
					possessivePronoun,
					handbookLink
				);
			} else if (useText2) {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text2,
					`${completeName}`,
					belowSummary,
					remainderText,
					pronoun,
					lowList,
					handbookLink,
					`${completeName}`
				);
			} else if (useText1) {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text1,
					`${completeName}`,
					belowSummary,
					remainderText,
					pronoun,
					lowList,
					handbookLink
				);
			} else {
				writeUpText = strings.formatString(
					strings.testView.childscrng_text4,
					`${completeName}`,
					`${withInZScoreCount + clearlyAboveZScoreCount}`
				);
			}
		} else {
			const stringForLowScoreInTwoSubtest = strings.formatString(
				strings.testView.localizedwriteuptextforLowScoreOfOrdiktatWithOneOtherSubTest,
				`${firstName}`
			);

			const findTestResult = testResult.subtests?.find((subtest) => {
				const subTestType = YOUTH_TEST_STEP_IDS.ORDDIKTAT;
				return subtest.id === subTestType;
			});

			// if the test is incomplete
			if (testResult.completedAt === null) {
				writeUpText = strings.formatString(
					strings.testView.localizedwriteuptextforIncompletedTest,
					`${completeName}`
				);
			} else if (
				zScoreLowCount === 2 &&
				allLowZscoresTestsWithNames.hasOwnProperty('ORDFORSTÅELSE') &&
				allLowZscoresTestsWithNames.hasOwnProperty('LESEPRØVEN')
			) {
				writeUpText = strings.formatString(
					strings.testView.localizedwriteuptextforLowScoreInLeseprovenAndOrdforstaelse,
					`${completeName}`,
					clearlyBelowZScoreCount && !significantlyBelowZScoreCount
						? strings.formatString(strings.testView.klartUnder, clearlyBelowZScoreCount)
						: '',
					significantlyBelowZScoreCount && !clearlyBelowZScoreCount
						? strings.formatString(strings.testView.betydligtUnder, significantlyBelowZScoreCount)
						: '',
					clearlyBelowZScoreCount && significantlyBelowZScoreCount
						? strings.formatString(strings.testView.klartOchBetydligtUnder, clearlyBelowZScoreCount, significantlyBelowZScoreCount)
						: '',
					withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
						? strings.formatString(strings.testView.resterandeDeltesterna, firstName)
						: '',
					`${getPronoun(testResult.testUser.gender)}`,
					handbookLink
				);
			} else if (
				findTestResult?.zScore <= -1 ||
				zScoreLowCount >= TESTS_REQUIRED_TO_PASS
			) {
				switch (true) {
					case zScoreLowCount === 1:
						writeUpText = strings.formatString(
							strings.testView.localizedwriteuptextforNormalresults,
							`${completeName}`,
							withInZScoreCount && !clearlyAboveZScoreCount
								? strings.testView.inom
								: '',
							clearlyAboveZScoreCount && !withInZScoreCount
								? strings.testView.klartOver
								: '',
							withInZScoreCount && clearlyAboveZScoreCount
								? strings.testView.inomOchKlartOver
								: '',
							`${withInZScoreCount + clearlyAboveZScoreCount}`,
							`${getPronoun(testResult.testUser.gender)}`
						);
						break;

					case zScoreLowCount === 2:
						writeUpText = strings.formatString(
							strings.testView.localizedwriteuptextforOneOtherSubTest,
							`${completeName}`,
							clearlyBelowZScoreCount &&
								!significantlyBelowZScoreCount
								? strings.formatString(strings.testView.klartUnder, clearlyBelowZScoreCount)
								: '',
							significantlyBelowZScoreCount &&
								!clearlyBelowZScoreCount
								? strings.formatString(strings.testView.betydligtUnder, significantlyBelowZScoreCount)
								: '',
							clearlyBelowZScoreCount &&
								significantlyBelowZScoreCount
								? strings.formatString(strings.testView.klartOchBetydligtUnder, clearlyBelowZScoreCount, significantlyBelowZScoreCount)
								: '',
							withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
								? strings.formatString(strings.testView.resterandeDeltesterna, firstName)
								: '',
							stringForLowScoreInTwoSubtest,
							handbookLink
						);
						break;

					case zScoreLowCount === 3:
						writeUpText = strings.formatString(
							strings.testView.localizedwriteuptextforTwoOtherSubTest,
							`${completeName}`,
							clearlyBelowZScoreCount &&
								!significantlyBelowZScoreCount
								? strings.formatString(strings.testView.klartUnder, clearlyBelowZScoreCount)
								: '',
							significantlyBelowZScoreCount &&
								!clearlyBelowZScoreCount
								? strings.formatString(strings.testView.betydligtUnder, significantlyBelowZScoreCount)
								: '',
							clearlyBelowZScoreCount &&
								significantlyBelowZScoreCount
								? strings.formatString(strings.testView.klartOchBetydligtUnder, clearlyBelowZScoreCount, significantlyBelowZScoreCount)
								: '',
							withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
								? strings.formatString(strings.testView.resterandeDeltesterna, firstName)
								: '',
							`${getPronoun(testResult.testUser.gender)}`,
							`${formateTestName(keys[0])}`,
							`${formateTestName(keys[1])}`,
							`${formateTestName(keys[2])}`,
							`${firstName}`,
							handbookLink
						);
						break;

					case zScoreLowCount > 3:
						if (
							zScoreLowCount === 6 ||
							zScoreLowCount === 5 ||
							zScoreLowCount === 4
						) {
							const lastElement = keys[keys.length - 1];
							writeUpText = strings.formatString(
								strings.testView.localizedwriteuptextforThreeORMoreOtherSubTest,
								`${completeName}`,
								clearlyBelowZScoreCount &&
									!significantlyBelowZScoreCount
									? strings.formatString(strings.testView.klartUnder, clearlyBelowZScoreCount)
									: '',
								significantlyBelowZScoreCount &&
									!clearlyBelowZScoreCount
									? strings.formatString(strings.testView.betydligtUnder, significantlyBelowZScoreCount)
									: '',
								clearlyBelowZScoreCount &&
									significantlyBelowZScoreCount
									? strings.formatString(strings.testView.klartOchBetydligtUnder, clearlyBelowZScoreCount, significantlyBelowZScoreCount)
									: '',
								withInZScoreCount > 0 ||
									clearlyAboveZScoreCount > 0
									? strings.formatString(strings.testView.resterandeDeltesterna, firstName)
									: '',
								`${getPronoun(testResult.testUser.gender)}`,
								`${formateTestName(keys[0])}`,
								`${formateTestName(keys[1])}`,
								`${formateTestName(keys[2])}`,
								keys[3]
									? keys[3] === lastElement
										? `och ${formateTestName(keys[3])}`
										: `, ${formateTestName(keys[3])}`
									: '',
								keys[4]
									? keys[4] === lastElement
										? `och ${formateTestName(keys[4])}`
										: `, ${formateTestName(keys[4])}`
									: '',
								keys[5]
									? keys[5] === lastElement
										? `och ${formateTestName(keys[5])}`
										: `, ${formateTestName(keys[5])}`
									: '',
								handbookLink
							);
						}
						break;
				}
			} else {
				writeUpText = strings.formatString(
					strings.testView.localizedwriteuptextforNormalresults,
					`${completeName}`,
					withInZScoreCount && !clearlyAboveZScoreCount ? strings.testView.inom : '',
					clearlyAboveZScoreCount && !withInZScoreCount
						? strings.testView.klartOver
						: '',
					withInZScoreCount && clearlyAboveZScoreCount
						? strings.testView.inomOchKlartOver
						: '',
					`${withInZScoreCount + clearlyAboveZScoreCount}`,
					`${getPronoun(testResult.testUser.gender)}`
				);
			}
		}

		return (
			<div className="sub-test__scores">
				<div className="width--half center">
					<h4 className="sub-test__heading big">
						{strings.testView.assessment}
					</h4>

					{/* show norms intro first for child-screening */}
					{isChildScreening && (
						<p style={{whiteSpace: 'pre-line'}}>
							{strings.testView.childscrng_norms_intro}
						</p>
					)}

					<p
						style={
							isChildScreening
								? {whiteSpace: 'pre-line'}
								: undefined
						}
					>
						{writeUpText}
					</p>
				</div>

				<div className="width--half center">
					{!isChildScreening && (
						<p>{strings.testView.textUnderEveryScreeningAssessment}</p>
					)}
				</div>
			</div>
		);
	}
}

Assessment.propTypes = {
	currentUser: PropTypes.shape({}),
};

const mapStateToProps = (state) => {
	const {currentUser} = state;
	return {currentUser};
};

export default connect(mapStateToProps)(Assessment);
